import { MinusOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Tabs, Upload, message } from "antd";
import PropTypes from "prop-types";
import React, { useState } from "react";

const { TabPane } = Tabs;

const RefernEarnAddUsersModal = ({ visible, onClose, onSave }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userRows, setUserRows] = useState([{ key: 0 }]);

  const handleAddRow = () => {
    const newKey = userRows.length;
    setUserRows([...userRows, { key: newKey }]);
  };

  const handleRemoveRow = (key) => {
    if (userRows.length > 1) {
      setUserRows(userRows.filter((row) => row.key !== key));
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const validUsers = Object.values(values.users || {}).filter(
        (user) => user && (user.host || user.email || user.phone)
      );

      if (validUsers.length === 0) {
        message.error("Please add at least one user");
        return;
      }

      const { isSaved } = await onSave(validUsers);
      if (isSaved) {
        form.resetFields();
        onClose();
      }
    } catch (error) {
      message.error("Failed to save users");
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUpload = (file) => {
    // check if the file is a csv file
    if (file.type !== "text/csv") {
      message.error("Please upload a valid CSV file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target.result;
      const lines = csvContent
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line);

      // Validate headers
      const expectedHeaders = ["host", "email", "phone"];
      const headers = lines[0].split(",").map((header) => header.trim());

      if (headers.length !== expectedHeaders.length) {
        message.error(
          "CSV must contain exactly 3 columns: host, email, and phone"
        );
        return;
      }

      // Check if headers match exactly and in correct order
      const headersMatch = headers.every(
        (header, index) => header === expectedHeaders[index]
      );
      if (!headersMatch) {
        message.error(
          "CSV headers must be exactly: host, email, phone in that order"
        );
        return;
      }

      const users = lines
        .slice(1)
        .map((line) => {
          const values = line.split(",").map((value) => value.trim());
          if (values.length !== expectedHeaders.length) {
            message.error("Invalid number of columns in a row");
            return null;
          }
          return headers.reduce((user, header, index) => {
            return { ...user, [header]: values[index] };
          }, {});
        })
        .filter((user) => user !== null); // Filter out any invalid rows

      onSave(users);
    };
    reader.readAsText(file);
  };

  return (
    <Modal
      title="Add Users"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
      zIndex={3000}
    >
      <Tabs defaultActiveKey="1">
        <TabPane tab="Upload CSV" key="1">
          <div style={{ padding: "20px 0" }}>
            <Upload
              accept=".csv"
              beforeUpload={handleCSVUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Upload CSV File</Button>
            </Upload>
            <p style={{ marginTop: 10, color: "#666" }}>
              Upload a CSV file containing user details (host, email, phone)
            </p>
          </div>
        </TabPane>

        <TabPane tab="Add Manually" key="2">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ padding: "20px 0" }}
          >
            {userRows.map((row) => (
              <div key={row.key} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 16 }}>
                  <Form.Item
                    name={["users", row.key, "host"]}
                    label="Host"
                    rules={[{ required: true, message: "Please enter host" }]}
                    style={{ flex: 1 }}
                  >
                    <Input placeholder="Enter host" />
                  </Form.Item>
                  <Form.Item
                    name={["users", row.key, "email"]}
                    label="Email"
                    rules={[
                      { required: true, message: "Please enter email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                    style={{ flex: 1 }}
                  >
                    <Input placeholder="Enter email" />
                  </Form.Item>
                  <Form.Item
                    name={["users", row.key, "phone"]}
                    label="Phone"
                    rules={[{ required: true, message: "Please enter phone" }]}
                    style={{ flex: 1 }}
                  >
                    <Input placeholder="Enter phone" />
                  </Form.Item>
                  {userRows.length > 1 && (
                    <Button
                      type="text"
                      danger
                      icon={<MinusOutlined />}
                      onClick={() => handleRemoveRow(row.key)}
                      style={{ marginTop: 30 }}
                    />
                  )}
                </div>
              </div>
            ))}

            <Button
              type="dashed"
              onClick={handleAddRow}
              icon={<PlusOutlined />}
              style={{ marginBottom: 24 }}
            >
              Add Another User
            </Button>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ width: "100%" }}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Modal>
  );
};
RefernEarnAddUsersModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
export default RefernEarnAddUsersModal;
